// Pure game logic for Book Club — a face–name association memory exercise.
// The player meets the members of a book club (a portrait photo, a name and
// a favourite kind of book), the cards are put away, and the player is quizzed
// on who is who. Everything here is side-effect free (randomness is injectable)
// so it can be unit tested.

// Question types, from easiest to hardest:
//   name   — see a face, pick their name
//   face   — see a name, pick their face
//   genre  — see a face, pick what they love to read
//   reader — see a kind of book, pick the face of who loves it
export const QUESTION_TYPES = ["name", "face", "genre", "reader"];

// Defaults used when the player's level carries no (valid) parameters.
export const DEFAULT_PARAMS = {
  members: 3,
  rounds: 3,
  options: 3,
  studyMsPerMember: 6000,
  similarity: 0,
  questionTypes: ["name"],
};

// Parse a level's parameters from its level_parameters JSON, e.g.
//   {"members": 4, "rounds": 3, "options": 3, "studyMsPerMember": 5500,
//    "similarity": 15, "questionTypes": ["name", "face"]}
// Missing or invalid fields fall back to DEFAULT_PARAMS. `similarity` is a
// percent (see dealClubs and generateMembers). Options are capped at the
// member count, since face options are always faces the player has met.
export const parseLevelParams = (level) => {
  let p = {};
  if (level && level.level_parameters) {
    try {
      p = JSON.parse(level.level_parameters) || {};
    } catch (e) {
      p = {};
    }
  }
  const pick = (key, min, max) => {
    const v = p[key];
    return Number.isInteger(v) && v >= min && v <= max ? v : DEFAULT_PARAMS[key];
  };
  const members = pick("members", 2, 8);
  const rounds = pick("rounds", 1, 10);
  const options = Math.min(pick("options", 2, 6), members);
  const studyMsPerMember = pick("studyMsPerMember", 1000, 20000);
  const similarity = pick("similarity", 0, 100);
  const types = Array.isArray(p.questionTypes)
    ? QUESTION_TYPES.filter((t) => p.questionTypes.includes(t))
    : [];
  const questionTypes = types.length ? types : DEFAULT_PARAMS.questionTypes;
  return { members, rounds, options, studyMsPerMember, similarity, questionTypes };
};

// A small seedable PRNG (mulberry32) so tests can replay a whole game.
export const seededRng = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const pickOne = (list, rng) => list[Math.floor(rng() * list.length)];

export const shuffle = (list, rng = Math.random) => {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// --- Members -------------------------------------------------------------

// First names by how the person in the photo presents (their faces/ folder).
// Names are dealt at random each game, so a face is never tied to one name.
export const NAMES = {
  f: ["Ada", "Bea", "Clara", "Daisy", "Elsie", "Fern", "Grace", "Hazel", "Iris", "June", "Kate", "Lily", "Mabel", "Nora", "Olive", "Pearl", "Rose", "Sylvia", "Tess", "Violet", "Wren", "Ivy", "Mae", "Ruth", "Alice", "Beth", "Carol", "Dora", "Edith", "Faye", "Gwen", "Holly", "Jane", "Lucy", "Maggie", "Nell", "Peggy", "Sally", "Una", "Vera"],
  m: ["Albert", "Ben", "Cyril", "Dennis", "Edgar", "Frank", "George", "Henry", "Isaac", "Jack", "Leo", "Miles", "Ned", "Oscar", "Percy", "Ray", "Sam", "Ted", "Victor", "Walter", "Hugh", "Felix", "Arthur", "Reg", "Alan", "Bill", "Colin", "Dave", "Eric", "Fred", "Gus", "Harry", "Ian", "Jim", "Ken", "Larry", "Max", "Neil", "Pete", "Roy"],
};

// Each kind of book has its own cover colour as well as an icon.
export const GENRES = [
  { id: "mystery", label: "Mysteries", icon: "🔍", color: "#3C4A6B" },
  { id: "romance", label: "Romance", icon: "💌", color: "#C84B6A" },
  { id: "scifi", label: "Science fiction", icon: "🚀", color: "#3C7E96" },
  { id: "fantasy", label: "Fantasy", icon: "🐉", color: "#8E6BB0" },
  { id: "history", label: "History", icon: "🏛️", color: "#8B5A2B" },
  { id: "poetry", label: "Poetry", icon: "🪶", color: "#B0607F" },
  { id: "adventure", label: "Adventure", icon: "🧭", color: "#D08713" },
  { id: "cooking", label: "Cookbooks", icon: "🍳", color: "#D6603E" },
  { id: "travel", label: "Travel", icon: "✈️", color: "#5EA9C4" },
  { id: "gardening", label: "Gardening", icon: "🌱", color: "#4F8A3D" },
  { id: "nature", label: "Nature", icon: "🦋", color: "#6E9F45" },
  { id: "comedy", label: "Comedy", icon: "😄", color: "#C9A227" },
];

export const genreById = (id) => GENRES.find((g) => g.id === id);

// Deal the faces for a whole game: `rounds` clubs of `size` different faces
// from the photo pool (entries {id, src, presentation}, see faces/index.js).
// Faces aren't reused within a game until the pool runs out. `similarity`
// (0-100) is the chance a club is all women or all men, which makes faces
// harder to tell apart; it needs enough photos of that group.
export const dealClubs = (pool, rounds, size, similarity = 0, rng = Math.random) => {
  const count = Math.min(size, pool.length);
  let deck = shuffle(pool, rng);
  const take = (fits) => {
    const club = [];
    while (club.length < count) {
      const i = deck.findIndex((f) => fits(f) && !club.includes(f));
      if (i === -1) {
        // Deck used up: start a fresh shuffle of the whole pool.
        if (!pool.some((f) => fits(f) && !club.includes(f))) break;
        deck = [...deck, ...shuffle(pool, rng)];
      } else {
        club.push(deck[i]);
        deck.splice(i, 1);
      }
    }
    return club;
  };

  const groups = ["f", "m"].filter(
    (g) => pool.filter((f) => f.presentation === g).length >= count,
  );
  const clubs = [];
  for (let r = 0; r < rounds; r++) {
    const alike = groups.length > 0 && rng() * 100 < similarity;
    const group = alike ? pickOne(groups, rng) : null;
    clubs.push(take((f) => !group || f.presentation === group));
  }
  return clubs;
};

// Introduce a club: each face gets a different first name that suits it and
// a different favourite kind of book. Below 50% similarity names also start
// with different letters, an extra cue that is taken away at harder levels.
export const generateMembers = (faces, similarity = 0, rng = Math.random) => {
  const usedNames = new Set();
  const usedInitials = new Set();
  const genres = shuffle(GENRES, rng);
  return faces.map((face, id) => {
    const names = NAMES[face.presentation] || NAMES.f;
    const pool = names.filter(
      (n) => !usedNames.has(n) && (similarity >= 50 || !usedInitials.has(n[0])),
    );
    const fallback = names.filter((n) => !usedNames.has(n));
    const name = pickOne(pool.length ? pool : fallback, rng);
    usedNames.add(name);
    usedInitials.add(name[0]);
    return { id, name, face, genre: genres[id % genres.length].id };
  });
};

// --- Quiz ----------------------------------------------------------------

// The correct answer plus distractors: other members' answers first (telling
// apart people you have met is the real test), then unmet fillers.
const buildOptions = (correct, fromMembers, fillers, count, rng) => {
  const distractors = shuffle(fromMembers.filter((x) => x !== correct), rng);
  const extra = shuffle(
    fillers.filter((x) => x !== correct && !distractors.includes(x)),
    rng,
  );
  return shuffle([correct, ...[...distractors, ...extra].slice(0, count - 1)], rng);
};

// One question per member, with the level's question types spread evenly
// across the members and asked in a random order.
export const buildQuiz = (members, questionTypes, optionCount, rng = Math.random) => {
  const types = shuffle(
    members.map((_, i) => questionTypes[i % questionTypes.length]),
    rng,
  );
  const memberIds = members.map((m) => m.id);
  const questions = members.map((member, i) => {
    const type = types[i];
    if (type === "name") {
      const names = members.map((m) => m.name);
      return {
        type,
        memberId: member.id,
        answer: member.name,
        options: buildOptions(member.name, names, NAMES[member.face.presentation] || NAMES.f, optionCount, rng),
      };
    }
    if (type === "genre") {
      return {
        type,
        memberId: member.id,
        answer: member.genre,
        options: buildOptions(
          member.genre,
          members.map((m) => m.genre),
          GENRES.map((g) => g.id),
          optionCount,
          rng,
        ),
      };
    }
    // face / reader: pick the right member out of the club.
    return {
      type,
      memberId: member.id,
      answer: member.id,
      options: buildOptions(member.id, memberIds, [], optionCount, rng),
    };
  });
  return shuffle(questions, rng);
};

// Session score = percent of questions answered correctly (0-100), matching
// the percent-correct thresholds the level promotion logic expects.
export const scoreGame = (correct, asked) =>
  asked > 0 ? Math.round((correct / asked) * 100) : 0;
